'use client';

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

export interface CollapsibleHeaderProps {
  /**
   * Rich, multi-line header content shown at the top of the panel. Scrolls
   * naturally with the rest of the content. Typically includes stage badge,
   * title, identifier, sub-line, and a metadata grid.
   */
  expanded: React.ReactNode;
  /**
   * Compact, single-row content shown as a sticky bar at the top of the
   * scroll container once the expanded view has scrolled out of view.
   * Typically just identifier + title + key status pill.
   */
  collapsed: React.ReactNode;
  className?: string;
}

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Two-mode header for detail panels (booking inspector, vehicle profile,
 * incident drawer). Renders an `expanded` slot at the top of the scroll
 * container and a `collapsed` slot as a sticky bar that fades in once the
 * expanded view scrolls past the top.
 *
 * Layout invariant: the collapsed bar reserves its full height in flow
 * underneath the expanded view (pulled back over the expanded's bottom
 * via a measured negative margin), so the content following the header
 * never reflows when the user scrolls between modes. The transition
 * between modes is a pure opacity swap — no document mutation.
 *
 * Render this as a direct child of the panel's scroll container. The
 * sticky behaviour binds to that container.
 *
 * @example
 * ```tsx
 * <ScrollArea>
 *   <CollapsibleHeader
 *     expanded={<VehicleDetailHeader vehicle={v} />}
 *     collapsed={<VehicleDetailHeaderCompact vehicle={v} />}
 *   />
 *   <DetailBody>…</DetailBody>
 * </ScrollArea>
 * ```
 */
export function CollapsibleHeader({ expanded, collapsed, className }: CollapsibleHeaderProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const collapsedRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);
  const [collapsedHeight, setCollapsedHeight] = useState(0);

  // Watch the sentinel (a 1 px marker placed at the bottom of the expanded
  // view). When it scrolls out of view, the user has passed the expanded
  // header — flip `stuck` so the collapsed bar fades in.
  //
  // The IO root is the nearest scrolling ancestor so this works inside a
  // panel with its own `overflow: auto`, not just on the document viewport.
  // `position: sticky` resolves its scroll ancestor the same way, so both
  // mechanisms stay in lockstep.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return;
    const root = getScrollParent(sentinel);
    const obs = new IntersectionObserver(([entry]) => setStuck(!entry.isIntersecting), {
      root: root === document.documentElement ? null : root,
      threshold: 0,
    });
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, []);

  // Measure the collapsed bar so we can pull it back over the bottom of
  // the expanded view with a matching negative margin — that way there's
  // no visible empty gap at-rest and no content shift when stuck.
  useIsomorphicLayoutEffect(() => {
    const el = collapsedRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      if (el) setCollapsedHeight(el.offsetHeight);
      return;
    }
    const obs = new ResizeObserver(() => setCollapsedHeight(el.offsetHeight));
    obs.observe(el);
    setCollapsedHeight(el.offsetHeight);
    return () => obs.disconnect();
  }, []);

  return (
    <Wrap className={className}>
      <Expanded data-stuck={stuck}>{expanded}</Expanded>
      <Sentinel ref={sentinelRef} aria-hidden />
      <CollapsedSticky
        ref={collapsedRef}
        data-stuck={stuck}
        aria-hidden={!stuck}
        $pullback={collapsedHeight}
      >
        {collapsed}
      </CollapsedSticky>
    </Wrap>
  );
}

/** Walk up the DOM until we hit an element whose computed `overflow-y`
 *  is `auto` or `scroll` — that's the element `position: sticky` is
 *  going to bind to, and what the IntersectionObserver should observe
 *  against. Falls back to `document.documentElement` when nothing in
 *  the ancestor chain scrolls. */
function getScrollParent(el: HTMLElement): HTMLElement {
  let cur: HTMLElement | null = el.parentElement;
  while (cur && cur !== document.documentElement) {
    const overflowY = window.getComputedStyle(cur).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') return cur;
    cur = cur.parentElement;
  }
  return document.documentElement;
}

/** `display: contents` so the wrap doesn't establish a containing block.
 *  The expanded, sentinel and sticky children flatten into the parent's
 *  flow, which means the sticky bar's containing block is the scroll
 *  container itself — and the bar stays pinned for the full panel scroll
 *  rather than just the header's own height. The wrap still exists in
 *  the DOM for testing / className pass-through; it's just visually
 *  transparent to layout. */
const Wrap = styled.div`
  display: contents;
`;

/** Expanded view sits in flow. When the collapsed bar takes over we fade
 *  the expanded out so the two never visually overlap. The expanded still
 *  occupies its full height in flow so content beneath cannot reflow. */
const Expanded = styled.div`
  transition: opacity 0.15s ease;
  &[data-stuck='true'] {
    /* Optional fade — collapsed already overlays the top so the expanded
       is hidden by the user scrolling past it. Leaving full opacity keeps
       it readable if the panel is short enough that the bottom edge is
       still on screen alongside the sticky bar. */
  }
`;

/** 1 px marker placed at the bottom edge of the expanded view. Observed by
 *  the IntersectionObserver to determine `stuck` state. Visually inert. */
const Sentinel = styled.div`
  height: 1px;
  margin-top: -1px;
  visibility: hidden;
  pointer-events: none;
`;

/** Sticky collapsed bar. Pulled back over the bottom of the expanded view
 *  via `margin-top: -h` (measured), so the wrap's total flow height equals
 *  the expanded's height — no gap, no jump.
 *
 *  Visibility is opacity-only; the element is always present in flow so
 *  any reflow caused by mounting/unmounting it is impossible. */
const CollapsedSticky = styled.div<{ $pullback: number }>`
  position: sticky;
  top: 0;
  z-index: 1;
  background: ${(p) => p.theme.colors.neutral.panel};
  margin-top: ${(p) => `-${p.$pullback}px`};
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
  border-bottom: 1px solid transparent;

  &[data-stuck='true'] {
    opacity: 1;
    pointer-events: auto;
    border-bottom-color: ${(p) => p.theme.colors.neutral.line};
  }
`;
